// src/components/programs/JobOrders/PrintJobVoucher.jsx
import React from "react";

const PrintJobVoucher = ({ jobVoucher, program, lng = "en", logoUrl }) => {
    if (!jobVoucher) return null;

    const isArabic = lng === "ar";
    const paxBreakdown = jobVoucher.pax_breakdown || [];

    const getPaxQty = (keyword) => {
        const type = paxBreakdown.find(p => {
            const enName = p.name?.en || (typeof p.name === 'string' ? p.name : '');
            const arName = p.name?.ar || '';
            return enName.toLowerCase().includes(keyword) || arName.includes(keyword);
        });
        return type ? type.quantity : 0;
    };

    const adults = getPaxQty('adult') || getPaxQty('بالغ');
    const children = getPaxQty('child') || getPaxQty('أطفال') || getPaxQty('طفل');
    const infants = getPaxQty('infant') || getPaxQty('رضيع');

    const thStyle = {
        backgroundColor: '#e8f5e9',
        color: '#1b5e20',
        fontWeight: 'bold',
        padding: '10px 8px',
        border: '1px solid #000',
        fontSize: '13px',
        whiteSpace: 'nowrap'
    };

    const tdStyle = {
        backgroundColor: '#fff',
        color: '#000',
        padding: '10px 8px',
        border: '1px solid #000',
        fontSize: '13px'
    };

    // Text helper
    const getText = (val) => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (typeof val === "number") return String(val);
        if (typeof val === "object") {
            const isAr = lng?.startsWith("ar");
            const primary = isAr ? "ar" : "en";
            const secondary = isAr ? "en" : "ar";
            if (val[lng] && typeof val[lng] === "string") return val[lng];
            if (val[primary] && typeof val[primary] === "string") return val[primary];
            if (val[secondary] && typeof val[secondary] === "string") return val[secondary];
            if (val.name) return getText(val.name);
            if (val.title) return getText(val.title);
            return "";
        }
        return String(val);
    };

    const serviceName = getText(jobVoucher.service_name);
    
    // Agent name snapshot or from program
    let agentName = "-";
    if (jobVoucher.agent) {
        if (typeof jobVoucher.agent === "object") {
            agentName = getText(jobVoucher.agent.name) || getText(jobVoucher.agent) || "-";
        } else {
            agentName = String(jobVoucher.agent);
        }
    } else if (program?.agent) {
        agentName = getText(program.agent.name) || "-";
    }

    // Customer name snapshot or from program description
    let customerName = "-";
    if (jobVoucher.customer) {
        if (typeof jobVoucher.customer === "object") {
            customerName = getText(jobVoucher.customer.name) || getText(jobVoucher.customer) || "-";
        } else {
            customerName = String(jobVoucher.customer);
        }
    } else if (jobVoucher.customer_name) {
        customerName = getText(jobVoucher.customer_name);
    } else if (program?.description) {
        customerName = getText(program.description);
    }

    const pickupTime = jobVoucher.pickup_time || jobVoucher.time || "-";
    const pickupLocation = getText(jobVoucher.pickup_location);
    const dropOffLocation = getText(jobVoucher.drop_off_location);
    const notesText = getText(jobVoucher.notes);
    const numberOfUnits = jobVoucher.number_of_units;

    return (
        <div style={{
            fontFamily: isArabic ? "'Cairo', sans-serif" : "'Inter', sans-serif",
            direction: isArabic ? "rtl" : "ltr",
            padding: "40px",
            color: "#333",
            backgroundColor: "#fff",
            lineHeight: 1.6
        }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                {logoUrl && <img src={logoUrl} alt="Logo" style={{ maxHeight: "100px", marginBottom: "10px" }} />}
                <h1 style={{ margin: 0, fontSize: "24px", color: "#1b5e20", textTransform: "uppercase" }}>
                    {isArabic ? "فاوتشر خدمة" : "SERVICE VOUCHER"}
                </h1>
            </div>

            {/* Main Table */}
            <table style={{ width: "100%", borderCollapse: "collapse", border: "2px solid #000" }}>
                <tbody>
                    <tr>
                        <td style={thStyle}>{isArabic ? "رقم الفاوتشر" : "VOUCHER NO."}</td>
                        <td style={tdStyle} colSpan={2}>{jobVoucher.voucher_number || "-"}</td>
                        <td style={thStyle}>{isArabic ? "رقم الجوب" : "JOB NO."}</td>
                        <td style={tdStyle} colSpan={2}>{jobVoucher.job_number || "-"}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "تاريخ الخدمة" : "SERVICE DATE"}</td>
                        <td style={tdStyle} colSpan={2}>{jobVoucher.activity_date || "-"}</td>
                        <td style={thStyle}>{isArabic ? "وقت الخدمة / الالتقاط" : "PICKUP / TIME"}</td>
                        <td style={tdStyle} colSpan={2}>{pickupTime}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "اسم العميل" : "CUSTOMER NAME"}</td>
                        <td style={tdStyle} colSpan={2}>{customerName}</td>
                        <td style={thStyle}>{isArabic ? "الوكيل" : "AGENT"}</td>
                        <td style={tdStyle} colSpan={2}>{agentName}</td>
                    </tr>
                    <tr>
                        <td style={thStyle}>{isArabic ? "الخدمة" : "SERVICE NAME"}</td>
                        <td style={tdStyle} colSpan={numberOfUnits !== null && numberOfUnits !== undefined ? 2 : 5}>
                            {serviceName || "-"}
                        </td>
                        {numberOfUnits !== null && numberOfUnits !== undefined && (
                            <>
                                <td style={thStyle}>{isArabic ? "عدد الوحدات" : "UNITS"}</td>
                                <td style={tdStyle} colSpan={2}>{numberOfUnits}</td>
                            </>
                        )}
                    </tr>

                    {/* Locations if present */}
                    {(pickupLocation || dropOffLocation) && (
                        <tr>
                            <td style={thStyle}>{isArabic ? "مكان الاستقبال" : "PICK UP LOCATION"}</td>
                            <td style={tdStyle} colSpan={2}>{pickupLocation || "-"}</td>
                            <td style={thStyle}>{isArabic ? "مكان التوصيل" : "DROP OFF LOCATION"}</td>
                            <td style={tdStyle} colSpan={2}>{dropOffLocation || "-"}</td>
                        </tr>
                    )}

                    {/* Pax Breakdown */}
                    <tr>
                        <td style={thStyle}>{isArabic ? "بالغين" : "ADULTS"}</td>
                        <td style={tdStyle} colSpan={1}>{adults}</td>
                        <td style={thStyle}>{isArabic ? "أطفال" : "CHILDREN"}</td>
                        <td style={tdStyle} colSpan={1}>{children}</td>
                        <td style={thStyle}>{isArabic ? "رُضع" : "INFANTS"}</td>
                        <td style={tdStyle} colSpan={1}>{infants}</td>
                    </tr>

                    {/* Cash on Tour (string accepted) */}
                    {jobVoucher.cash_on_tour !== undefined && jobVoucher.cash_on_tour !== null && jobVoucher.cash_on_tour !== "" && (
                        <tr>
                            <td style={thStyle}>{isArabic ? "مبلغ التحصيل (نقداً)" : "CASH ON TOUR"}</td>
                            <td style={tdStyle} colSpan={5}>
                                <strong style={{ color: "#d32f2f", fontSize: "14px" }}>
                                    {String(jobVoucher.cash_on_tour)}
                                </strong>
                            </td>
                        </tr>
                    )}

                    {/* Confirmation / Booking */}
                    <tr>
                        <td style={thStyle}>{isArabic ? "حجز بواسطة" : "BOOKED BY"}</td>
                        <td style={tdStyle} colSpan={2}>{jobVoucher.booked_by?.name || "-"}</td>
                        <td style={thStyle}>{isArabic ? "تأكيد بواسطة" : "CONFIRMED BY"}</td>
                        <td style={tdStyle} colSpan={2}>{jobVoucher.confirmed_by?.name || jobVoucher.print_candidate?.name || "-"}</td>
                    </tr>

                    {/* Notes & Remarks */}
                    <tr>
                        <td style={thStyle}>{isArabic ? "ملاحظات" : "REMARKS"}</td>
                        <td style={{ ...tdStyle, padding: "12px 14px", verticalAlign: "top" }} colSpan={5}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {notesText && (
                                    <div style={{
                                        fontWeight: "600",
                                        color: "#1b5e20",
                                        fontSize: "13px",
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-line"
                                    }}>
                                        {notesText}
                                    </div>
                                )}
                                {!notesText && <span style={{ color: "#888" }}>-</span>}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Note: STRICT RULE - Never display Room Number or Booking Code on Voucher */}
        </div>
    );
};

export default PrintJobVoucher;

